import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import QRCode from "qrcode";

import { Btn } from "@/components/ui/Btn/Btn";
import { Icon } from "@/components/ui/Icon/Icon";
import { Input } from "@/components/ui/Input/Input";
import { useAuth } from "@/lib/AuthContext";
import { api, ApiError } from "@/lib/api";
import {
  useDisable2fa,
  useStart2fa,
  useTwoFactorStatus,
  useVerify2fa,
} from "@/lib/queries";
import styles from "./Account.module.css";

interface MeResponse {
  sub: string;
  email: string | null;
  emailVerified: boolean;
  groups: string[];
  isAdmin: boolean;
}

type Tab = "profile" | "2fa";

function tabFromHash(hash: string): Tab {
  return hash === "#2fa" ? "2fa" : "profile";
}

/**
 * Personal-account surface. Two real tabs today (Profile + Two-factor
 * authentication); Sessions / Notifications from §4.19 are intentionally
 * NOT shown — they'd be empty placeholders. The 2FA tab is the canonical
 * surface for software-token MFA enrolment + disable.
 */
export function Account() {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>(tabFromHash(location.hash));

  useEffect(() => {
    setTab(tabFromHash(location.hash));
  }, [location.hash]);

  function switchTab(next: Tab) {
    navigate(
      { hash: next === "profile" ? "" : `#${next}` },
      { replace: true },
    );
  }

  if (auth.status === "loading") {
    return <main className={styles.main}>Loading…</main>;
  }
  if (auth.status === "anonymous") {
    return <Navigate to="/signin?next=/account" replace />;
  }

  return (
    <main className={styles.main}>
      <div className={styles.inner}>
        <p className={styles.eyebrow}>Account</p>
        <h1 className={styles.title}>Manage your account</h1>

        <div className={styles.tabs} role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "profile"}
            className={`${styles.tab} ${tab === "profile" ? styles.tabActive : ""}`}
            onClick={() => switchTab("profile")}
          >
            Profile
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "2fa"}
            className={`${styles.tab} ${tab === "2fa" ? styles.tabActive : ""}`}
            onClick={() => switchTab("2fa")}
          >
            Two-factor authentication
          </button>
        </div>

        {tab === "profile" && <ProfileTab />}
        {tab === "2fa" && <TwoFactorTab email={auth.user?.email ?? null} />}

        <div className={styles.actions}>
          <Btn onClick={() => auth.signOut()} variant="secondary" size="md">
            Sign out
          </Btn>
        </div>
      </div>
    </main>
  );
}

function ProfileTab() {
  const meQuery = useQuery<MeResponse, ApiError>({
    queryKey: ["me"],
    queryFn: () => api<MeResponse>("/me"),
    retry: false,
  });

  return (
    <section className={styles.tabPanel}>
      <p className={styles.body}>
        Below is the round-trip response from <code>GET /api/me</code> — your
        Cognito ID token was validated by the API Lambda and these claims
        came back.
      </p>

      {meQuery.isLoading && <p className={styles.muted}>Fetching /api/me…</p>}

      {meQuery.error && (
        <pre className={styles.error}>
          {meQuery.error.status} {meQuery.error.message}
          {meQuery.error.body
            ? `\n\n${JSON.stringify(meQuery.error.body, null, 2)}`
            : ""}
        </pre>
      )}

      {meQuery.data && (
        <dl className={styles.facts}>
          <div className={styles.fact}>
            <dt>Subject (Cognito sub)</dt>
            <dd>
              <code>{meQuery.data.sub}</code>
            </dd>
          </div>
          <div className={styles.fact}>
            <dt>Email</dt>
            <dd>{meQuery.data.email ?? <em>(none)</em>}</dd>
          </div>
          <div className={styles.fact}>
            <dt>Email verified</dt>
            <dd>{meQuery.data.emailVerified ? "yes" : "no"}</dd>
          </div>
          <div className={styles.fact}>
            <dt>Groups</dt>
            <dd>
              {meQuery.data.groups.length === 0 ? (
                <em>(none)</em>
              ) : (
                meQuery.data.groups.map((g) => <code key={g}>{g}</code>)
              )}
            </dd>
          </div>
          <div className={styles.fact}>
            <dt>Platform admin</dt>
            <dd>{meQuery.data.isAdmin ? "yes" : "no"}</dd>
          </div>
        </dl>
      )}
    </section>
  );
}

/**
 * Self-service software-token MFA. Three states:
 *  - status loading → spinner.
 *  - enabled = true → "Two-factor authentication is on" + Disable button.
 *  - enabled = false → optional setup flow.
 */
function TwoFactorTab({ email }: { email: string | null }) {
  const statusQuery = useTwoFactorStatus();

  if (statusQuery.isLoading) {
    return (
      <section className={styles.tabPanel}>
        <p className={styles.muted}>Checking your two-factor status…</p>
      </section>
    );
  }

  if (statusQuery.error) {
    return (
      <section className={styles.tabPanel}>
        <pre className={styles.error}>
          {statusQuery.error.status} {statusQuery.error.message}
        </pre>
      </section>
    );
  }

  const enabled = statusQuery.data?.enabled === true;

  return (
    <section className={styles.tabPanel}>
      {enabled ? <TwoFactorOn /> : <TwoFactorSetup email={email} />}
    </section>
  );
}

function TwoFactorOn() {
  const disable = useDisable2fa();
  const [confirming, setConfirming] = useState(false);

  return (
    <div className={styles.tfaState}>
      <div className={styles.tfaHeader}>
        <span className={styles.tfaIconOk} aria-hidden="true">
          <Icon name="lock" size={18} />
        </span>
        <div>
          <h2 className={styles.tfaTitle}>Two-factor authentication is on</h2>
          <p className={styles.muted}>
            You'll be asked for a 6-digit code from your authenticator app each
            time you sign in.
          </p>
        </div>
      </div>

      {!confirming ? (
        <div className={styles.tfaRow}>
          <Btn
            variant="ghost"
            onClick={() => setConfirming(true)}
            iconLeft={<Icon name="alert" size={13} />}
          >
            Disable two-factor authentication
          </Btn>
        </div>
      ) : (
        <div className={styles.tfaConfirm}>
          <p className={styles.muted}>
            Disabling will let anyone with your password sign in without a
            second factor. Continue?
          </p>
          <div className={styles.tfaRow}>
            <Btn
              variant="ghost"
              onClick={() => setConfirming(false)}
              disabled={disable.isPending}
            >
              Cancel
            </Btn>
            <Btn
              variant="primary"
              onClick={() => disable.mutate()}
              loading={disable.isPending}
              iconLeft={<Icon name="alert" size={13} />}
            >
              Yes, disable
            </Btn>
          </div>
          {disable.error && (
            <p className={styles.fieldError}>
              <Icon name="alert" size={13} /> {disable.error.message}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function TwoFactorSetup({ email }: { email: string | null }) {
  const start = useStart2fa();
  const verify = useVerify2fa();
  const [code, setCode] = useState("");
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);

  const secretCode = start.data?.secretCode;

  // Re-render the QR whenever Cognito hands us a fresh secret.
  useEffect(() => {
    if (!secretCode) {
      setQrDataUrl(null);
      return;
    }
    const issuer = "Notamen";
    const label = email ?? "Account";
    const otpauth =
      `otpauth://totp/${encodeURIComponent(issuer)}:${encodeURIComponent(label)}` +
      `?secret=${secretCode}&issuer=${encodeURIComponent(issuer)}`;
    let cancelled = false;
    QRCode.toDataURL(otpauth, { width: 220, margin: 1 })
      .then((url) => {
        if (!cancelled) setQrDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setQrDataUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [secretCode, email]);

  if (!secretCode) {
    return (
      <div className={styles.tfaState}>
        <div className={styles.tfaHeader}>
          <span className={styles.tfaIconOff} aria-hidden="true">
            <Icon name="lock" size={18} />
          </span>
          <div>
            <h2 className={styles.tfaTitle}>Set up two-factor authentication</h2>
            <p className={styles.muted}>
              Add a one-time code from an authenticator app (Google
              Authenticator, 1Password, Authy, etc.) to your sign-in. Optional
              — not required.
            </p>
          </div>
        </div>

        <div className={styles.tfaRow}>
          <Btn
            variant="primary"
            onClick={() => start.mutate()}
            loading={start.isPending}
            iconLeft={<Icon name="smartphone" size={13} />}
          >
            Get started
          </Btn>
        </div>

        {start.error && (
          <p className={styles.fieldError}>
            <Icon name="alert" size={13} /> {start.error.message}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className={styles.tfaState}>
      <h2 className={styles.tfaTitle}>Scan the QR code with your app</h2>
      <p className={styles.muted}>
        Open your authenticator app and scan the QR code below. Then type the
        6-digit code it shows to finish setup.
      </p>

      <div className={styles.tfaScan}>
        {qrDataUrl ? (
          <img
            src={qrDataUrl}
            alt="Two-factor authentication QR code"
            className={styles.tfaQr}
            width={220}
            height={220}
          />
        ) : (
          <div className={styles.tfaQrFallback} aria-hidden="true" />
        )}
        <div className={styles.tfaSecret}>
          <p className={styles.muted}>Can't scan? Type this code into your app:</p>
          <code className={styles.tfaSecretCode}>{secretCode}</code>
        </div>
      </div>

      <form
        className={styles.tfaForm}
        onSubmit={(e) => {
          e.preventDefault();
          if (code.length === 6) verify.mutate({ code });
        }}
        noValidate
      >
        <label className={styles.fieldLabel} htmlFor="tfa-code">
          6-digit code
        </label>
        <Input
          id="tfa-code"
          icon="lock"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          placeholder="000000"
        />
        {verify.error && (
          <p className={styles.fieldError}>
            <Icon name="alert" size={13} /> {verify.error.message}
          </p>
        )}
        <div className={styles.tfaRow}>
          <Btn
            variant="ghost"
            type="button"
            onClick={() => {
              start.reset();
              verify.reset();
              setCode("");
            }}
            disabled={verify.isPending}
          >
            Cancel
          </Btn>
          <Btn
            variant="primary"
            type="submit"
            loading={verify.isPending}
            disabled={code.length !== 6}
            iconLeft={<Icon name="check" size={13} />}
          >
            Verify and turn on
          </Btn>
        </div>
      </form>
    </div>
  );
}
